// MemberListItem.js - Component for individual member card in the list
import React from 'react';
import PropTypes from 'prop-types';
import { 
  Card, 
  CardMedia, 
  Typography, 
  Box, 
  Tooltip,
  Badge,
  styled,
  Chip
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import { getAvatarUrl } from '../../../../api/utils';
import noAvatar from '../../../../assets/images/no-avatar.png';

// Styled components
const MemberCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.1)'
  }
}));

const AvatarContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '100%', // 1:1 Aspect ratio
  overflow: 'hidden'
}));

const StyledCardMedia = styled(CardMedia)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column'
}));

const StatusBadge = styled(Badge)(({ theme, status }) => {
  let badgeColor;
  
  switch(status) {
    case 'AC':
      badgeColor = theme.palette.success.main;
      break;
    case 'PP':
      badgeColor = theme.palette.warning.main;
      break;
    case 'IN':
      badgeColor = theme.palette.error.main;
      break;
    default:
      badgeColor = theme.palette.grey[500];
  }
  
  return {
    '& .MuiBadge-badge': {
      backgroundColor: badgeColor,
      color: badgeColor,
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        content: '""'
      }
    }
  };
});

const RoleChip = styled(Chip)(({ theme, role }) => {
  let chipColor;
  
  switch(role) {
    case 'AD':
      chipColor = theme.palette.primary.main;
      break;
    case 'FO':
      chipColor = theme.palette.secondary.main;
      break;
    case 'MO':
      chipColor = theme.palette.info.main;
      break;
    default:
      chipColor = theme.palette.grey[500];
  }
  
  return {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: chipColor,
    fontWeight: 600,
    fontSize: '0.625rem',
    height: 20,
    '& .MuiChip-label': {
      padding: '0 6px'
    }
  };
});

const MemberListItem = ({ member, setSelectedParticipant, role }) => {
  const handleImageRenderError = (e) => {
    e.target.src = noAvatar;
  };

  // Get role label for display
  const getRoleLabel = (roleCode) => {
    switch(roleCode) {
      case 'AD':
        return 'Admin';
      case 'FO':
        return 'Founder';
      case 'MO':
        return 'Mod';
      case 'PA':
        return 'Part';
      case 'GU':
        return 'Guest';
      default:
        return 'Member';
    }
  };

  // Get status icon based on member status
  const getStatusIcon = () => {
    if (member.status === 'PP' && role === 'admin') {
      return (
        <Tooltip title="Pending approval">
          <WarningIcon sx={{ color: 'warning.main', fontSize: 16, ml: 0.5 }} />
        </Tooltip>
      );
    } else if (member.status === 'AC') {
      return (
        <Tooltip title="Verified member">
          <VerifiedIcon sx={{ color: 'success.main', fontSize: 16, ml: 0.5 }} />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <Tooltip title="Click to view profile" arrow>
      <MemberCard onClick={() => setSelectedParticipant(member)}>
        <StatusBadge
          overlap="circular"
          variant="dot"
          status={member.status}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ width: '100%', height: '100%' }}
        >
          <AvatarContainer>
            <StyledCardMedia
              component="img"
              src={getAvatarUrl(member?.user, noAvatar)}
              alt={`${member?.user?.first_name || 'Member'} profile`}
              onError={handleImageRenderError}
            />
            {member.role && (
              <RoleChip 
                label={getRoleLabel(member.role)}
                size="small"
                role={member.role}
              />
            )}
          </AvatarContainer>
        </StatusBadge>

        <ContentContainer>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="subtitle2" 
              noWrap 
              sx={{ 
                fontWeight: 600,
                flex: 1 
              }}
            >
              {member?.user?.first_name || 'N/A'}
            </Typography>
            {getStatusIcon()}
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            noWrap
            sx={{ 
              mt: 0.5,
              fontSize: '0.75rem'
            }}
          >
            {member?.user?.alias || member?.user?.last_name || '-'}
          </Typography>
          
          {member?.user?.tags && member?.user?.tags.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={member.user.tags[0].name}
                size="small"
                sx={{ 
                  height: 18, 
                  fontSize: '0.625rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.06)'
                }}
              />
              {member.user.tags.length > 1 && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  +{member.user.tags.length - 1}
                </Typography>
              )}
            </Box>
          )}
        </ContentContainer>
      </MemberCard>
    </Tooltip>
  );
};

MemberListItem.propTypes = {
  member: PropTypes.object.isRequired,
  setSelectedParticipant: PropTypes.func.isRequired,
  role: PropTypes.string
};

export default MemberListItem;